import React, { useEffect, useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useRouter } from 'next/compat/router';
import { useApolloClient } from '@apollo/client';

import { StatusTypes } from 'src/gql/graphql';
import type { ShowEnrollmentQuery } from 'src/gql/graphql';

import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import useInstoreCart from 'hooks/use-instore-cart';
import useUser from 'hooks/use-user';
import ShowEnrollment from 'src/payments/graphql/queries/show-enrollment.gql';
import useUI from 'hooks/use-ui';
import useTranslation from 'hooks/use-translation';
import { tracker } from 'utils/analytics';
import MetaTags from 'components/meta-tags';
import useFeatureFlags from 'hooks/use-feature-flags';
import { SignupLayout } from 'src/payments/layout/signup/layout';
import { getLogger } from 'src/utils/datadog-logger';
import { useDutchiePayAnalytics } from 'src/payments/hooks';
import { SuccessView } from 'src/payments/dutchie-pay/signup/views/success';
import { AccountChurnStep } from 'src/payments/dutchie-pay/signup/views/account-churn/account-churn-step';

import { AggregatorWidget } from 'src/payments/dutchie-pay/signup/views/aggregator-widget';
import { FullPageLoader } from 'shared/components';
import { PlaidEmbeddedSearch } from 'src/payments/components';
import { CompleteAccount } from 'src/payments/dutchie-pay/signup/views/complete-account';
import { useFinalizePbbAfterSignup } from 'src/payments/dutchie-pay/signup/hooks/use-finalize-pbb-after-signup';
import { PayByBankOAuthLogin } from 'src/payments/signup/views/oath-form/oauth-login';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import {
  EmailForm,
  PasswordForm,
  SignupForm,
  Loading,
  TermsView,
  BankAccountSelector,
  ResetPasswordForm,
  UnsupportedAccount,
} from './views';
import {
  EnrollmentSteps,
  tosSteps,
  connectingBankSteps,
  editBankAccount,
  CHECKOUT_PATH,
  relinkBankAccount,
  PlaidLinkCustomizationNames,
  LOG_NAMESPACES,
} from '../../constants';
import { AeroSyncWidget } from './views/aerosync-widget';

const logger = getLogger(LOG_NAMESPACES.PBB_ENROLLMENT);

const {
  connectBank,
  emailForm,
  forgotPasswordForm,
  passwordForm,
  signUpForm,
  terms,
  bankAccountSelector,
  bankAccountsLoading,
  tosLoading,
  unsupportedAccount,
  alreadySignedUp,
  success,
  completeAccount,
  accountChurn,
} = EnrollmentSteps;

export type ViewTextProps = {
  titleText?: string;
  subtitleText?: string;
};

const DutchiePaySignup = (): JSX.Element | null => {
  const router = useRouter();
  const apolloClient = useApolloClient();
  const { step: queryStringStep, cname, oauth_state_id: plaidOath } = router?.query ?? {};
  const editBankAccountEnabled = queryStringStep === editBankAccount;
  const relinkBankAccountEnabled = queryStringStep === relinkBankAccount;
  const { dispensary, loading: dispensaryLoading, error: dispensaryFetchError } = useDispensary();

  const User = useUser();
  const UI = useUI();

  const { t } = useTranslation();
  const FeatureFlags = useFeatureFlags();
  const featureFlagsLoading = FeatureFlags.loading;
  const { trackEnrollmentStepViewed } = useDutchiePayAnalytics();
  const { isEnrolledDutchiePay } = useDutchiePay();
  const dutchiePayEnrollment = useDutchiePayEnrollment();
  const enableOmniauthLogin = dispensary?.storeSettings?.enableOmniauthLoginCheckout ?? false;
  const { isKiosk, checkoutToken } = useInstoreCart();

  // Observer hooks
  const userExists = useObserver(() => User?.exists);
  const userPhone = useObserver(() => User?.phone);
  const userBirthday = useObserver(() => User?.birthday);
  const userLastName = useObserver(() => User?.lastName);
  const isSsoUser = useObserver(() => User?.isSsoUser);
  const enrollmentStatus = useObserver(() => User?.payByBankEnrollmentStatus);
  const currentStep = useObserver(() => dutchiePayEnrollment?.currentStep);
  const refUrl = useObserver(() => dutchiePayEnrollment?.refUrl);
  const entryPoint = useObserver(() => dutchiePayEnrollment?.entryPoint);

  const isMissingAccountInfo = useMemo(() => !userPhone || !userBirthday || !userLastName, [
    userPhone,
    userBirthday,
    userLastName,
  ]);

  const requiresRedirect = useMemo(
    () =>
      !!refUrl &&
      (currentStep === EnrollmentSteps.completeEnrollment || currentStep === EnrollmentSteps.alreadySignedUp),
    [refUrl, currentStep]
  );

  const userCameFromCheckout = entryPoint === CHECKOUT_PATH;
  const userCameFromDutchiePlusCheckout = refUrl?.startsWith('/checkouts/') ?? false;
  const userCameFromInstore = refUrl?.startsWith('/pay/instore') ?? false;
  const {
    shouldFinalize: shouldFinalizePbbSwitch,
    isReady: pbbFinalizeReady,
    hasFailed: pbbFinalizeFailed,
    finalizeAndRedirect: finalizePbbAndRedirect,
  } = useFinalizePbbAfterSignup();
  const isAeroSyncEnabled = FeatureFlags.flags[`fintech.platform.enable-aerosync-enrollment.rollout`] ?? false;
  const isUsingExternalLoginProvider =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- intentional boolean OR, not a nullish default
    !!dispensary?.id && (userCameFromDutchiePlusCheckout || userCameFromInstore) && enableOmniauthLogin;

  const viewText = {
    email: {
      title: t('dutchiePay.email.title', 'Enter email to continue'),
      subTitle: t('dutchiePay.email.subtitle', "We'll check your email to login or sign up."),
    },
    signup: {
      title: t('dutchiePay.signup.title', 'Create your account'),
      subTitle: t('dutchiePay.signup.subtitle', 'Enter the info below to create your account.'),
    },
  };

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
  const queryRefUrl = typeof router?.query?.refUrl === 'string' ? router?.query.refUrl : '';
  const showSuccessScreen = currentStep === success || (currentStep === alreadySignedUp && !refUrl);

  useEffect(() => {
    User.setAgeOverride(99, false);
  }, [User]);

  useEffect(() => {
    if (dispensaryFetchError) {
      logger.error('Failed to fetch dispensary data during signup initialization', {
        error: dispensaryFetchError,
        cname: cname ?? 'unknown',
        step: queryStringStep ?? 'unknown',
        refUrl: refUrl ?? 'unknown',
        oauth_state_id: plaidOath ?? 'unknown',
      });
    }
  }, [dispensaryFetchError, cname, queryStringStep, refUrl, plaidOath]);

  useEffect(() => {
    tracker.setContext({
      activeIsUserEnrolledDutchiePay: enrollmentStatus === StatusTypes.statusEnrolled,
      isKioskOrder: isKiosk,
      checkoutToken,
    });
  }, [enrollmentStatus, isKiosk, checkoutToken]);

  // Redirect after enrollment completes. When the shopper started enrollment from the order
  // confirmation PBB CTA (`pbbReopenPending` + `viewOrderId`), hand off to /pay/instore
  // directly instead of bouncing through `refUrl`. If the order load fails or times out we
  // fall back to the legacy refUrl push so the user is never stranded on the success screen.
  useEffect(() => {
    if (!requiresRedirect) {
      return;
    }
    if (shouldFinalizePbbSwitch && !pbbFinalizeFailed) {
      if (!pbbFinalizeReady) {
        return;
      }
      void finalizePbbAndRedirect(refUrl);
      return;
    }
    void router?.push(refUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiresRedirect, refUrl, shouldFinalizePbbSwitch, pbbFinalizeReady, pbbFinalizeFailed]); // FIXME: ENG-32714 fix hooks dependency

  useEffect(() => {
    if (plaidOath) {
      dutchiePayEnrollment.plaidOauthReceived();
    } else if (!isEnrolledDutchiePay || relinkBankAccountEnabled || editBankAccountEnabled) {
      if (userExists && isMissingAccountInfo && (isSsoUser || isUsingExternalLoginProvider)) {
        dutchiePayEnrollment.setCurrentStep(completeAccount);
      } else {
        dutchiePayEnrollment.beginEnrollment(userExists, queryRefUrl || refUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userExists, isMissingAccountInfo, isSsoUser, isUsingExternalLoginProvider]);

  useEffect(() => {
    if (currentStep === EnrollmentSteps.alreadySignedUp) {
      trackEnrollmentStepViewed({ step: EnrollmentSteps.alreadySignedUp });
    }
  }, [currentStep, trackEnrollmentStepViewed]);

  const setRefUrl = (): void => {
    try {
      dutchiePayEnrollment.setRefUrl(queryRefUrl);
    } catch (err) {
      throw new Error(`Failed to set ref URL: ${String(err.message)}`);
    }
  };

  const handleAccountChurnCancel = async (): Promise<void> => {
    try {
      const { data } = await apolloClient.query<ShowEnrollmentQuery>({
        query: ShowEnrollment,
        fetchPolicy: 'no-cache',
      });
      const { enrollmentStatus: freshEnrollmentStatus, paymentMethods } = data.showEnrollment;
      User.setUserPayByBankEnrollmentStatus({
        enrollmentStatus: freshEnrollmentStatus,
        paymentMethods,
        relinkOrEditBankAccountEnabled: false,
      });
    } catch (err) {
      logger.error('Failed to refresh enrollment status after account churn cancel', { error: err });
    } finally {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- refUrl can be empty string; ?? would not fall back for ''
      void router?.push(refUrl || '/pay/signup');
    }
  };

  const handleAccountChurnLinkBank = (): void => {
    const { step: _step, ...restQuery } = router?.query ?? {};
    void router?.replace({ query: restQuery });
    dutchiePayEnrollment.relinkFailed();
  };

  const updateRouter = async (): Promise<void> => {
    try {
      await router?.replace({
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
        query: { ...router?.query, refUrl },
      });
    } catch (err) {
      throw new Error(`Failed to update router: ${String(err.message)}`);
    }
  };

  useEffect(() => {
    try {
      setRefUrl();
      if (refUrl) {
        void updateRouter();
      }
    } catch (err) {
      // Just logging errors for now to see if there are any issues routing away from the signup page
      logger.error('refUrl error during enrollment', {
        error: err?.message ?? 'Unknown error',
        errorCode: err?.code ?? 'unknown',
        queryRefUrl,
        refUrl: refUrl ?? 'unknown',
        dispensaryId: dispensary?.id ?? 'unknown',
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryRefUrl, refUrl]);

  useEffect(() => {
    if (dispensary) {
      dutchiePayEnrollment.setDispensary(dispensary, UI.variant);
    }
    if (refUrl.includes(CHECKOUT_PATH)) {
      dutchiePayEnrollment.entryPoint = CHECKOUT_PATH;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispensary, refUrl]);

  if (dispensaryLoading || featureFlagsLoading || (plaidOath && currentStep === emailForm)) {
    return <FullPageLoader />;
  }

  // User is auto redirected to the external login provider in the OAuth flow
  if (currentStep === emailForm && isUsingExternalLoginProvider) {
    return (
      <>
        <FullPageLoader />
        <PayByBankOAuthLogin dispensaryId={dispensary?.id} />
      </>
    );
  }

  return (
    <>
      <MetaTags />
      {currentStep === connectBank && isAeroSyncEnabled && <AeroSyncWidget />}
      <SignupLayout>
        {/* Forms */}
        {currentStep === emailForm && !isUsingExternalLoginProvider && (
          <EmailForm titleText={viewText.email.title} subtitleText={viewText.email.subTitle} />
        )}
        {currentStep === passwordForm && (
          <PasswordForm showToolTip={userCameFromCheckout && !dutchiePayEnrollment.dispensary.useWhiteLabel} />
        )}
        {currentStep === forgotPasswordForm && <ResetPasswordForm />}
        {currentStep === signUpForm && (
          <SignupForm titleText={viewText.signup.title} subtitleText={viewText.signup.subTitle} />
        )}
        {currentStep === connectBank &&
          !isAeroSyncEnabled &&
          (dutchiePayEnrollment.isInstoreSignup && !relinkBankAccountEnabled ? (
            <PlaidEmbeddedSearch linkCustomizationName={PlaidLinkCustomizationNames.EMBEDDED_SEARCH_INSTORE} />
          ) : (
            <AggregatorWidget relinkBankAccountEnabled={relinkBankAccountEnabled} />
          ))}

        {/* This step is used when using Google login and we are missing info on the account */}
        {currentStep === completeAccount && <CompleteAccount />}

        {/* TODO: See if this view can be removed */}
        {currentStep === bankAccountSelector && <BankAccountSelector />}

        {currentStep === terms && <TermsView />}
        {/* TODO: See if this view can be removed */}
        {currentStep === unsupportedAccount && <UnsupportedAccount />}

        {currentStep === accountChurn && (
          <AccountChurnStep onCancel={handleAccountChurnCancel} onLinkBank={handleAccountChurnLinkBank} />
        )}

        {showSuccessScreen && <SuccessView />}
        {/* loaders */}
        {currentStep === bankAccountsLoading && <Loading steps={connectingBankSteps} />}
        {/* TODO: See if this view can be removed */}
        {currentStep === tosLoading && <Loading steps={tosSteps} />}
      </SignupLayout>
    </>
  );
};
export default DutchiePaySignup;
